import { type Dispatch, useEffect, useRef, useCallback, useState } from 'react'
import { Button, type ButtonProps } from './button/Button.tsx'
import { Icon } from '~/components/ui/icons/icon.tsx'
import Spinner from '../spinner.tsx'
import { ErrorList, type ListOfErrors } from './forms.tsx'

export interface FileInputProps {
  fileChooserButtonProps?: Omit<ButtonProps, 'onClick'>
  submitButtonProps?: Omit<ButtonProps, 'disabled'>
  validation: {
    fileTypes: string
    maxFileSize: number
  }
  isUploading?: boolean
  onUploadClick?: () => void
  onReject: (reason: string) => void

  // Props to use outside a form
  error?: ListOfErrors
  acceptedFile?: File | null | undefined
  onAccept?: Dispatch<File | null>
  onCancel?: () => void
}

export default function FileInput({
  fileChooserButtonProps,
  submitButtonProps,
  error,
  acceptedFile,
  validation,
  isUploading,
  onAccept,
  onReject,
  onCancel,
  onUploadClick,
  ...inputProps
}: FileInputProps & Partial<React.InputHTMLAttributes<HTMLInputElement>>) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [validatedFile, setValidatedFile] = useState(acceptedFile)

  function handleDropzoneDrop(e: any) {
    e.preventDefault()
    if (e.dataTransfer.files.length > 0) {
      const files: File[] = Array.from(e.dataTransfer.files)
      handleFiles(files)
    }
  }

  const isValidFile = useCallback(
    (file: File) => {
      const { fileTypes, maxFileSize } = validation
      const fileExtension = file.name.split('.').pop()?.toLowerCase()
      const fileType = file.type

      const validExtensions = fileTypes
        .split(',')
        .map(ext => ext.trim().replace(/^\./, ''))
      let validMimeTypes: string[] = []

      if (fileExtension && mimeTypesDictionary[fileExtension]) {
        validMimeTypes = mimeTypesDictionary[fileExtension]
      }

      if (
        fileTypes &&
        !validExtensions.includes(String(fileExtension)) &&
        !validMimeTypes.includes(fileType)
      ) {
        return { valid: false, reason: 'Formato de archivo incorrecto' }
      }

      if (maxFileSize && file.size > maxFileSize) {
        return { valid: false, reason: 'El archivo es demasiado grande' }
      }

      return { valid: true, reason: '' }
    },
    [validation],
  )

  const handleFiles = useCallback(
    (files: File[]) => {
      if (files && files.length) {
        const file = files[0]
        const { valid, reason } = isValidFile(file)
        if (valid) {
          setValidatedFile(file)

          if (fileInputRef.current) {
            const dataTransfer = new DataTransfer()
            dataTransfer.items.add(file)
            fileInputRef.current.files = dataTransfer.files
          }

          if (onAccept) {
            onAccept(file)
          }
        } else {
          onReject(reason)
        }
      }
    },
    [isValidFile, onAccept, onReject],
  )

  const onLoadFile = useCallback(
    (e: Event) => {
      const files = Array.from((e.target as HTMLInputElement).files || [])
      handleFiles(files)
    },
    [handleFiles],
  )

  const onCancelFile = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation()

    setValidatedFile(null)

    if (onCancel) {
      onCancel()
    }
  }

  useEffect(() => {
    let localFileInputRef: HTMLInputElement | null = null
    if (fileInputRef.current) localFileInputRef = fileInputRef.current

    if (localFileInputRef) {
      localFileInputRef.addEventListener('change', onLoadFile)

      return () => localFileInputRef?.removeEventListener('change', onLoadFile)
    }
  }, [onLoadFile])

  return (
    <div>
      <div onDragOver={e => e.preventDefault()} onDrop={handleDropzoneDrop}>
        <Button
          type="button"
          className={`relative min-h-28 w-full rounded-3xl border-2 border-primary bg-[#F5F5FA] ${
            validatedFile ? 'border-solid' : 'border-dashed'
          }`}
          {...fileChooserButtonProps}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="flex items-center gap-2 text-primary">
            <Icon
              size="lg"
              name={validatedFile ? 'file' : 'upload-files-small'}
            ></Icon>

            <p className="text-md">
              {validatedFile
                ? validatedFile.name
                : 'Haz click para seleccionar o arrastra un archivo'}
            </p>
          </div>
          <input
            {...inputProps}
            accept={validation.fileTypes}
            className="hidden h-full w-full"
            ref={fileInputRef}
            type="file"
          />
          {validatedFile && (
            <Icon
              name="close"
              size="drawer"
              className="absolute right-2 top-2"
              onClick={onCancelFile}
            />
          )}
        </Button>
      </div>
      <p className="mt-1 text-sm text-gray-700">
        {validation.fileTypes.replace(',', ' ')} / Peso máximo:{' '}
        {formatFileSize(validation.maxFileSize)}
      </p>
      {error && (
        <div className="px-4  pt-1">
          <ErrorList errors={error} />
        </div>
      )}
      {onUploadClick && isUploading ? (
        <Button
          disabled={Boolean(!validatedFile || error)}
          onClick={onUploadClick}
          {...submitButtonProps}
          className="mt-3 w-full flex-row justify-center"
        >
          {isUploading ? <Spinner size={6} color="text-gray-200" /> : 'Subir'}
        </Button>
      ) : (
        <></>
      )}
    </div>
  )
}

const formatFileSize = (maxSize: number) => {
  if (maxSize >= 1024 * 1024) {
    return `${Math.round(maxSize / (1024 * 1024))} MB`
  }
  return `${Math.round(maxSize / 1024)} KB`
}

const mimeTypesDictionary: Record<string, string[]> = {
  xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  ods: [
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/x-vnd.oasis.opendocument.spreadsheet',
    'application/vnd.sun.xml.calc',
  ],
  csv: [
    'text/csv',
    'application/csv',
    'text/x-csv',
    'application/x-csv',
    'text/comma-separated-values',
    'text/tab-separated-values',
  ],
  sheet: [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/x-vnd.oasis.opendocument.spreadsheet',
    'application/vnd.sun.xml.calc',
    'application/vnd.oasis.opendocument.spreadsheet',
  ],
  pdf: [
    'application/pdf',
    'application/x-pdf',
    'application/acrobat',
    'applications/vnd.pdf',
    'text/pdf',
    'text/x-pdf',
  ],
}
